import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { awsStreamsApi } from '@/lib/aws-api-client';
import { queryKeys } from '@/lib/query-client';
import type { Stream } from '@/types/models';

/**
 * React Query hook for fetching streams
 *
 * Streams change less frequently than quotes, so we can cache them longer.
 * For new users, automatically triggers sample stream creation and polls until they appear.
 */
export function useStreams(enabled = true) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const [isAwaitingSamples, setIsAwaitingSamples] = useState(false);
  const pollStartTime = useRef<number | null>(null);
  const hasTriggeredSampleCreation = useRef(false);
  const sampleCreationInProgress = useRef(false);

  const query = useQuery({
    queryKey: queryKeys.streams.all,
    queryFn: async (): Promise<Stream[]> => {
      if (!session?.user?.email) {
        return [];
      }

      // Call AWS API Gateway directly with JWT authentication
      const response = await awsStreamsApi.list();

      return response.streams || [];
    },
    enabled: enabled && !!session?.user?.email,

    // Streams don't change often, cache them longer
    staleTime: 10 * 60 * 1000, // 10 minutes

    // Poll every 2s when awaiting sample streams
    refetchInterval: isAwaitingSamples ? 2000 : false,
  });

  // Reset trigger when streams appear (allows re-trigger if user deletes all streams)
  useEffect(() => {
    if (query.data && query.data.length > 0) {
      hasTriggeredSampleCreation.current = false;
    }
  }, [query.data]);

  // Auto-trigger sample creation when user has no streams
  useEffect(() => {
    const streams = query.data || [];
    const isLoading = query.isLoading;
    const userEmail = session?.user?.email;

    // Debug logging
    console.log('[SampleCreation] Check:', {
      isLoading,
      streamsCount: streams.length,
      hasTriggered: hasTriggeredSampleCreation.current,
      inProgress: sampleCreationInProgress.current,
      userEmail: userEmail ? 'set' : 'not set',
    });

    // Only trigger when: not loading, no streams, not already triggered, authenticated
    if (
      !isLoading &&
      streams.length === 0 &&
      !hasTriggeredSampleCreation.current &&
      !sampleCreationInProgress.current &&
      userEmail
    ) {
      console.log('[SampleCreation] Triggering POST /streams/samples');
      hasTriggeredSampleCreation.current = true;
      sampleCreationInProgress.current = true;

      // Call sample creation API
      awsStreamsApi.createSamples()
        .then((data) => {
          console.log('[SampleCreation] Response:', data);
          sampleCreationInProgress.current = false;
          if (data.created) {
            // Start polling for new streams
            setIsAwaitingSamples(true);
            pollStartTime.current = Date.now();
            // Invalidate to trigger immediate refetch
            queryClient.invalidateQueries({ queryKey: queryKeys.streams.all });
          }
        })
        .catch((error) => {
          console.error('[SampleCreation] Error:', error);
          sampleCreationInProgress.current = false;
        });
    }
  }, [query.data, query.isLoading, session?.user?.email, queryClient]);

  // Manage polling state for awaiting sample streams
  useEffect(() => {
    const streams = query.data || [];

    // Streams appeared - stop polling
    if (streams.length > 0 && isAwaitingSamples) {
      console.log('[SampleCreation] Streams appeared, stopping poll');
      setIsAwaitingSamples(false);
      pollStartTime.current = null;
      return;
    }

    // Stop polling after 30 seconds (sample copy should be done by then)
    if (isAwaitingSamples && pollStartTime.current) {
      const elapsed = Date.now() - pollStartTime.current;
      if (elapsed > 30000) {
        console.log('[SampleCreation] Timeout, stopping poll');
        setIsAwaitingSamples(false);
        pollStartTime.current = null;
      }
    }
  }, [query.data, isAwaitingSamples]);

  return {
    ...query,
    isAwaitingSamples,
  };
}

/**
 * Mutation hook for creating a new stream
 */
export function useCreateStream() {
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  return useMutation({
    mutationFn: async (streamData: {
      stream_config: {
        stream_id: string;
        stream_name: string;
        stream_type?: string;
        description?: string;
        target_audience?: string;
        tags?: string[];
        source_url?: string;
        source_instructions?: string;
        ai_prompt?: string;
        raw_template?: string;
        youtube_category?: string;
        category_id?: string;
        defaults?: Record<string, any>;
      };
      prompt_template: {
        template_id: string;
        stream_id: string;
        template: string;
        raw_template: string;
        source_type?: string;
        generation_params?: {
          temperature: number;
          max_tokens: number;
        };
      };
    }) => {
      if (!session?.user?.email) throw new Error('Not authenticated');
      
      // Call AWS API Gateway directly with JWT authentication
      return await awsStreamsApi.create(streamData);
    },
    
    onSuccess: (newStream) => {
      toast.success('Stream created successfully');
      
      // Optimistically add the new stream to cache
      queryClient.setQueryData<Stream[]>(
        queryKeys.streams.all,
        (oldStreams = []) => [...oldStreams, newStream]
      );
      
      // Also refetch to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: queryKeys.streams.all });
    },
    
    onError: (error) => {
      toast.error('Failed to create stream');
      console.error('Stream creation error:', error);
    }
  });
}

/**
 * Mutation hook for creating sample streams (first-time user setup)
 * Called by frontend when a new user has no streams
 */
export function useCreateSampleStreams() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return await awsStreamsApi.createSamples();
    },

    onSuccess: (data) => {
      if (data.created) {
        // Invalidate to trigger refetch - polling will handle waiting for samples
        queryClient.invalidateQueries({ queryKey: queryKeys.streams.all });
      }
    },

    onError: (error) => {
      console.error('Failed to create sample streams:', error);
    }
  });
}

/**
 * Mutation hook for updating a stream
 */
export function useUpdateStream() {
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  return useMutation({
    mutationFn: async ({ streamId, ...updateData }: { streamId: string; name?: string; description?: string }) => {
      if (!session?.user?.email) throw new Error('Not authenticated');
      
      // Call AWS API Gateway directly with JWT authentication
      return await awsStreamsApi.update(streamId, updateData);
    },
    
    // Optimistic update
    onMutate: async ({ streamId, ...updateData }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.streams.all });
      const previousStreams = queryClient.getQueryData<Stream[]>(queryKeys.streams.all);
      
      queryClient.setQueryData<Stream[]>(
        queryKeys.streams.all,
        (oldStreams = []) => oldStreams.map(stream => 
          stream.stream_id === streamId 
            ? { ...stream, ...updateData }
            : stream
        )
      );
      
      return { previousStreams };
    },
    
    onSuccess: () => {
      toast.success('Stream updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.streams.all });
    },
    
    onError: (error, variables, context) => {
      if (context?.previousStreams) {
        queryClient.setQueryData(queryKeys.streams.all, context.previousStreams);
      }
      toast.error('Failed to update stream');
      console.error('Stream update error:', error);
    }
  });
}

/**
 * Mutation hook for deleting a stream
 */
export function useDeleteStream() {
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  return useMutation({
    mutationFn: async (streamId: string) => {
      if (!session?.user?.email) throw new Error('Not authenticated');
      
      // Call AWS API Gateway directly with JWT authentication
      return await awsStreamsApi.delete(streamId);
    },
    
    // Optimistic update - remove from cache immediately
    onMutate: async (streamId: string) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.streams.all });
      const previousStreams = queryClient.getQueryData<Stream[]>(queryKeys.streams.all);
      
      queryClient.setQueryData<Stream[]>(
        queryKeys.streams.all,
        (oldStreams = []) => oldStreams.filter(stream => stream.stream_id !== streamId)
      );
      
      return { previousStreams };
    },
    
    onSuccess: () => {
      toast.success('Stream deleted successfully');
      
      // Also invalidate quote queries since quotes are tied to streams
      queryClient.invalidateQueries({ queryKey: queryKeys.quotes.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.streams.all });
    },
    
    onError: (error, streamId, context) => {
      if (context?.previousStreams) {
        queryClient.setQueryData(queryKeys.streams.all, context.previousStreams);
      }
      toast.error('Failed to delete stream');
      console.error('Stream deletion error:', error);
    }
  });
}